<template>
  <div class="recycle-order-list-container">
    <!-- 页面标题 -->
    <div class="page-title">桶内回收订单管理</div>

    <!-- 筛选区域 -->
    <el-card class="search-card" shadow="hover">
      <el-form :model="searchForm" inline @submit.prevent="getOrderList">
        <el-form-item label="订单编号">
          <el-input
            v-model="searchForm.orderNo"
            placeholder="请输入订单编号"
            clearable
            style="width: 200px;"
          />
        </el-form-item>
        <el-form-item label="用户手机号">
          <el-input
            v-model="searchForm.phone"
            placeholder="请输入用户手机号"
            clearable
            style="width: 200px;"
          />
        </el-form-item>
        <el-form-item label="回收机设备号">
          <el-input
            v-model="searchForm.deviceNo"
            placeholder="请输入回收机设备号"
            clearable
            style="width: 200px;"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="searchForm.status" placeholder="请选择订单状态">
            <el-option label="审核中" value="1" />
            <el-option label="已完成" value="2" />
            <el-option label="已拒绝" value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="投递时间">
          <el-date-picker
            unlink-panels
            v-model="searchForm.createTimeRange"
            value-format="yyyy-MM-dd HH:mm:ss"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            style="width: 350px;"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="getOrderList">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 订单列表 -->
    <el-card class="list-card" shadow="hover" style="margin-top: 20px;">
      <div v-loading="loading" style="min-height: 400px;">
        <el-table
          :data="orderList"
          border
          stripe
          style="width: 100%;"
          row-key="id"
        >
          <el-table-column prop="orderNo" label="订单编号" width="190" align="center" />
          <el-table-column prop="phone" label="用户手机号" width="110" align="center">
            <template slot-scope="scope">
              {{ scope.row.phone}}
            </template>
          </el-table-column>
          <el-table-column prop="deviceName" label="回收机名称" width="100" align="center">
            <template slot-scope="scope">
              {{ scope.row.deviceName}}
            </template>
          </el-table-column>
          <el-table-column prop="deviceNo" label="回收机设备号" width="110" align="center">
            <template slot-scope="scope">
              {{ scope.row.deviceNo}}
            </template>
          </el-table-column>
          <el-table-column prop="deviceAddress" label="回收机地址"  align="center">
            <template slot-scope="scope">
              {{ scope.row.deviceAddress}}
            </template>
          </el-table-column>
          <el-table-column
            prop="recycleType"
            label="回收类型"
            width="120"
            align="center"
          >
            <template slot-scope="scope">
              <el-tag size="mini">
                可回收物
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="weight" label="重量(kg)" width="100" align="center">
            <template slot-scope="scope">
              {{ scope.row.weight || 0 }}
            </template>
          </el-table-column>
          <el-table-column prop="unitPrice" label="单价（元/kg）" width="110" align="center">
            <template slot-scope="scope">
              {{ scope.row.unitPrice || 0 }}
            </template>
          </el-table-column>
          <el-table-column prop="totalPrice" label="金额(元)" width="100" align="center">
            <template slot-scope="scope">
              {{ (scope.row.totalPrice || 0).toFixed(2) }}
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="投递时间" width="190" align="center">
            <template slot-scope="scope">
              {{ formatTime(scope.row.createTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="订单状态" width="170" align="center">
            <template slot-scope="scope">
              {{ scope.row.status === 1 ? '审核中' : scope.row.status === 2 ? '已完成' : '已拒绝' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                @click.stop="toDetail(scope.row)"
              >
                详情
              </el-button>
              <!-- 审核按钮：仅审核中状态显示 -->
              <el-button
                size="mini"
                type="warning"
                @click.stop="openAuditDialog(scope.row)"
                v-if="scope.row.status === 1"
                style="margin-left: 5px;"
              >
                审核
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          style="text-align: right; margin-top: 20px;"
        />
      </div>
    </el-card>

    <!-- 审核弹框 -->
    <el-dialog
      title="订单审核"
      :visible.sync="auditDialogVisible"
      width="50%"
      @close="resetAuditForm"
    >
      <el-form :model="auditForm" label-width="120px" :rules="auditRules"
      ref="auditFormRef">
        <!-- 订单信息展示 -->
        <el-form-item label="订单编号">
          <span>{{ auditForm.orderNo }}</span>
        </el-form-item>
        <el-form-item label="用户手机号">
          <span>{{ auditForm.phone }}</span>
        </el-form-item>
        <el-form-item label="回收机名称">
          <span>{{ auditForm.deviceName }}</span>
        </el-form-item>
        <el-form-item label="回收机编号">
          <span>{{ auditForm.deviceNo }}</span>
        </el-form-item>
        <el-form-item label="回收重量(kg)">
          <span>{{ auditForm.weight || 0 }}</span>
        </el-form-item>
        <el-form-item label="订单金额(元)">
          <span>{{ (auditForm.totalPrice || 0).toFixed(2) }}</span>
        </el-form-item>
        <el-form-item label="投递时间">
          <span>{{ formatTime(auditForm.createTime) }}</span>
        </el-form-item>
        <!-- 审核状态选择 -->
        <el-form-item label="审核结果" prop="status" required>
          <el-radio-group v-model="auditForm.status">
            <el-radio label="2">审核通过</el-radio>
            <el-radio label="3">审核拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 审核备注（可选） -->
        <el-form-item label="审核备注">
          <el-input
            v-model="auditForm.auditRemark"
            type="textarea"
            placeholder="请输入审核备注（选填）"
            rows="3"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="auditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAudit">提交审核</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { getWlRecycleDeviceOrderList,auditOrder } from "@/api/recyclingMachine";
export default {
  name: 'RecycleOrderList',
  data() {
    return {
      loading: false,
      orderList: [],
      // 筛选表单
      searchForm: {
        orderNo: '',
        phone: '',
        deviceNo: '',
        status: '',
        createTimeRange: [], // 时间范围筛选
        createTimeBegin: '',
        createTimeEnd: '',
      },
      // 分页参数
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0,
      },
      // 审核弹框相关
      auditDialogVisible: false,
      auditForm: {
        id: '', // 订单ID
        orderNo: '',
        phone: '',
        deviceName: '',
        deviceNo: '',
        weight: '',
        totalPrice: '',
        createTime: '',
        status: '', // 2-通过 3-拒绝
      },
      auditRules: {
        status: [
          { required: true, message: '请选择审核结果', trigger: 'change' }
        ]
      }
    };
  },
  created() {
    this.getOrderList();
  },
  methods: {
    // 时间格式化
    formatTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-';
    },

    // 获取订单列表
    async getOrderList() {
      this.loading = true;
      try {
        // 构造筛选参数
        const params = {
          pageNo: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
          orderNo: this.searchForm.orderNo.trim ? this.searchForm.orderNo.trim() : '',
          phone: this.searchForm.phone ? this.searchForm.phone.trim() : '',
          deviceNo: this.searchForm.deviceNo ? this.searchForm.deviceNo.trim() : '',
          status: this.searchForm.status,
          // 时间范围转换为开始/结束时间
          createTimeBegin: this.searchForm.createTimeRange && this.searchForm.createTimeRange[0]
            ? moment(this.searchForm.createTimeRange[0]).format('YYYY-MM-DD HH:mm:ss')
            : '',
          createTimeEnd: this.searchForm.createTimeRange && this.searchForm.createTimeRange[1]
            ? moment(this.searchForm.createTimeRange[1]).format('YYYY-MM-DD HH:mm:ss')
            : '',
        };
        const res = await getWlRecycleDeviceOrderList(params);
        if (res.code === 200) {
          this.orderList = res.data.list || res.data.records || [];
          this.pagination.total = res.data.total || 0;
        }
      } catch (error) {
        console.error('获取订单列表失败：', error);
        this.$message.error('订单数据加载失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    // 重置筛选条件
    resetSearch() {
      this.searchForm = {
        orderNo: '',
        deviceNo: '',
        createTimeRange: [],
        status: '',
        phone: '',
        createTimeBegin: '',
        createTimeEnd: '',
      };
      this.pagination.pageNum = 1;
      this.getOrderList();
    },

    // 分页-每页条数变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.pageNum = 1;
      this.getOrderList();
    },

    // 分页-页码变化
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.getOrderList();
    },

    // 跳转到订单详情页
    toDetail(row) {
      this.$router.push({
        name: 'RecycleOrderDetail',
        params: { id: row.id },
      });
    },

    // 打开审核弹框
    openAuditDialog(row) {
      // 赋值订单信息到审核表单
      this.auditForm = {
        id: row.id,
        orderNo: row.orderNo,
        phone: row.phone,
        deviceName: row.deviceName,
        deviceNo: row.deviceNo,
        weight: row.weight,
        totalPrice: row.totalPrice,
        createTime: row.createTime,
        status: '', // 初始化审核状态
        auditRemark: ''
      };
      this.auditDialogVisible = true;
      
    },

    // 重置审核表单
    resetAuditForm() {
      this.auditForm = {
        id: '',
        orderNo: '',
        phone: '',
        deviceName: '',
        deviceNo: '',
        weight: '',
        totalPrice: '',
        createTime: '',
        status: '',
        auditRemark: ''
      };
      this.$refs.auditFormRef.clearValidate();
    },

    // 提交审核
    async submitAudit() {
      if (!this.auditForm.status) {
        this.$message.warning('请选择审核结果');
        return;
      }
      try {
        // 构造审核参数
        const params = {
          id: this.auditForm.id,
          status: this.auditForm.status,
          auditRemark: this.auditForm.auditRemark
        };
        const res = await auditOrder(params);
        if (res.code === 200) {
          this.$message.success('审核成功');
          this.auditDialogVisible = false;
          this.getOrderList(); 
        } else {
          this.$message.error('审核失败：' + res.msg);
        }
      } catch (error) {
        console.error('审核失败：', error);
        this.$message.error('审核失败，请重试');
      }
    }
  },
};
</script>

<style scoped>
.recycle-order-list-container {
  padding: 20px;
  background: #f7f7f7;
  min-height: calc(100vh - 60px);
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
}
.search-card {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
}
.list-card {
  background: #fff;
  border-radius: 4px;
}
.dialog-footer {
  text-align: right;
}
</style>